import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Cpu, Filter, Search, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidates,
  useDictamenesList,
  useExpedientesList,
  useGroupsList,
  useStandardsList,
  useUsersByRole,
} from "@/store/hooks";

type EstadoFiltro = "todos" | "pendiente" | "competente" | "todavia-no-competente";

export default function DictaminadorBandeja() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("dictaminador"), [setRole]);

  const expedientes = useExpedientesList();
  const candidatesMap = useCandidates();
  const groups = useGroupsList();
  const standards = useStandardsList();
  const evaluadores = useUsersByRole("evaluador");
  const dictamenes = useDictamenesList();

  const [estandar, setEstandar] = useState<string>("todos");
  const [grupo, setGrupo] = useState<string>("todos");
  const [evaluadorId, setEvaluadorId] = useState<string>("todos");
  const [estado, setEstado] = useState<EstadoFiltro>("pendiente");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");
  const [query, setQuery] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const filtered = useMemo(() => {
    return expedientes
      .filter((e) => {
        if (estandar !== "todos" && e.estandar !== estandar) return false;
        if (grupo !== "todos" && e.grupoId !== grupo) return false;
        if (estado !== "todos" && e.dictamen !== estado) return false;
        if (evaluadorId !== "todos") {
          const g = groupById.get(e.grupoId);
          const evals = g?.evaluadoresIds ?? (g?.evaluadorId ? [g.evaluadorId] : []);
          if (!evals.includes(evaluadorId)) return false;
        }
        const fecha = e.evaluacionPractica.fecha;
        if (desde && fecha && fecha !== "—" && fecha < desde) return false;
        if (hasta && fecha && fecha !== "—" && fecha > hasta) return false;
        if (query.trim()) {
          const cand = candidatesMap[e.candidatoId];
          const hay =
            (cand?.nombre ?? "").toLowerCase().includes(query.toLowerCase()) ||
            (cand?.curp ?? "").toLowerCase().includes(query.toLowerCase()) ||
            e.id.toLowerCase().includes(query.toLowerCase());
          if (!hay) return false;
        }
        return true;
      })
      .sort((a, b) => (a.evaluacionPractica.fecha < b.evaluacionPractica.fecha ? 1 : -1));
  }, [expedientes, estandar, grupo, estado, evaluadorId, desde, hasta, query, groupById, candidatesMap]);

  const pendingCount = expedientes.filter(
    (e) => e.dictamen === "pendiente" && e.evaluacionPractica.aplicada,
  ).length;
  const recent = useMemo(
    () =>
      dictamenes
        .filter((d) => d.resultado === "competente" && !d.revocacion)
        .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
        .slice(0, 5),
    [dictamenes],
  );

  function resetFilters() {
    setEstandar("todos");
    setGrupo("todos");
    setEvaluadorId("todos");
    setEstado("pendiente");
    setDesde("");
    setHasta("");
    setQuery("");
  }

  return (
    <AppLayout pageTitle="Bandeja de dictamen">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Bandeja de dictaminación</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            {pendingCount} expedientes pendientes · {filtered.length} resultados con filtros activos
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link href="/dictaminador/historial" data-testid="link-historial">
            <PrimaryButton variant="outline">Ver historial</PrimaryButton>
          </Link>
          <PrimaryButton variant="outline" testId="button-filter" onClick={() => setShowFilters((v) => !v)}>
            <Filter size={13} /> {showFilters ? "Ocultar filtros" : "Filtros"}
          </PrimaryButton>
        </div>
      </div>

      <Card style={{ marginBottom: 14, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Search size={14} color="var(--cc-gray-500)" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por candidato, CURP o expediente..."
            data-testid="input-search"
            style={{ flex: 1, padding: "6px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13 }}
          />
          {(query || estandar !== "todos" || grupo !== "todos" || evaluadorId !== "todos" || estado !== "pendiente" || desde || hasta) && (
            <button
              onClick={resetFilters}
              data-testid="button-reset-filters"
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", background: "var(--cc-gray-100)", border: "none", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
            >
              <X size={12} /> Limpiar
            </button>
          )}
        </div>
        {showFilters && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginTop: 12 }}>
            <FilterSelect label="Estándar" value={estandar} onChange={setEstandar} options={[{ v: "todos", l: "Todos" }, ...standards.map((s) => ({ v: s.codigo, l: s.codigo }))]} testId="filter-estandar" />
            <FilterSelect label="Grupo" value={grupo} onChange={setGrupo} options={[{ v: "todos", l: "Todos" }, ...groups.map((g) => ({ v: g.id, l: g.nombre }))]} testId="filter-grupo" />
            <FilterSelect label="Evaluador" value={evaluadorId} onChange={setEvaluadorId} options={[{ v: "todos", l: "Todos" }, ...evaluadores.map((u) => ({ v: u.id, l: u.nombre }))]} testId="filter-evaluador" />
            <FilterSelect label="Estado" value={estado} onChange={(v) => setEstado(v as EstadoFiltro)} options={[
              { v: "todos", l: "Todos" },
              { v: "pendiente", l: "Pendiente" },
              { v: "competente", l: "Competente" },
              { v: "todavia-no-competente", l: "Todavía no competente" },
            ]} testId="filter-estado" />
            <div style={{ display: "flex", gap: 6 }}>
              <DateInput label="Desde" value={desde} onChange={setDesde} testId="filter-desde" />
              <DateInput label="Hasta" value={hasta} onChange={setHasta} testId="filter-hasta" />
            </div>
          </div>
        )}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <Card padding={0}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
            <SectionTitle>Expedientes ({filtered.length})</SectionTitle>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
                {["Expediente", "Candidato", "Estándar", "Grupo", "Práctica", "Estado", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((exp) => {
                const cand = candidatesMap[exp.candidatoId];
                const g = groupById.get(exp.grupoId);
                const tone =
                  exp.dictamen === "competente" ? "green" : exp.dictamen === "todavia-no-competente" ? "red" : "amber";
                const label =
                  exp.dictamen === "competente" ? "Competente" : exp.dictamen === "todavia-no-competente" ? "Todavía no" : "Pendiente";
                const sinPractica = !exp.evaluacionPractica.aplicada;
                return (
                  <tr key={exp.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }} data-testid={`row-exp-${exp.id}`}>
                    <td style={{ padding: "10px 14px", fontFamily: "var(--app-font-mono)", color: "var(--cc-navy)", fontWeight: 700 }}>{exp.id.toUpperCase()}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{cand?.nombre ?? "—"}</div>
                      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{cand?.unidad ?? ""}</div>
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)" }}>{exp.estandar}</td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)", fontSize: 12 }}>{g?.nombre ?? "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--cc-gray-700)" }}>
                      {sinPractica ? "—" : `${exp.evaluacionPractica.criteriosCompetente}/${exp.evaluacionPractica.criteriosTotales}`}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <Badge tone={tone as "green" | "red" | "amber"}>{label}</Badge>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {exp.certificado.emitido ? (
                        <Link href={`/dictaminador/certificado/${exp.id}`} data-testid={`link-cert-${exp.id}`} style={{ color: "var(--cc-teal)", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Ver certificado →</Link>
                      ) : sinPractica ? (
                        <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>Sin práctica</span>
                      ) : (
                        <Link href={`/dictaminador/expediente/${exp.id}`} data-testid={`link-open-${exp.id}`} style={{ color: "var(--cc-teal)", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Dictaminar →</Link>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: "30px 14px", textAlign: "center", fontSize: 13, color: "var(--cc-gray-500)" }}>
                    Sin expedientes que coincidan con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Link href="/dictaminador/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
            <Card style={{ cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <Cpu size={15} color="var(--cc-teal)" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Pregúntale a CertiAI</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>
                Pre-procesó <strong>{pendingCount} expedientes</strong> pendientes con las reglas vigentes de cada estándar. Abre el chat para consultarlas o resumir la bandeja.
              </div>
            </Card>
          </Link>

          <Card>
            <SectionTitle>Certificados emitidos recientes</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recent.map((d) => {
                const cand = candidatesMap[d.candidatoId];
                return (
                  <Link key={d.id} href={`/dictaminador/certificado/${d.expedienteId}`} data-testid={`link-recent-${d.expedienteId}`} style={{ textDecoration: "none" }}>
                    <div style={{ padding: 10, border: "1px solid var(--cc-gray-100)", borderRadius: 8, cursor: "pointer" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{cand?.nombre ?? d.candidatoId}</div>
                      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{d.certificadoId ?? "—"} · {d.fecha.slice(0, 10)}</div>
                    </div>
                  </Link>
                );
              })}
              {recent.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Aún no se emiten certificados.</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function FilterSelect({ label, value, onChange, options, testId }: { label: string; value: string; onChange: (v: string) => void; options: { v: string; l: string }[]; testId?: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={{ padding: "6px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 12, background: "white" }}>
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}

function DateInput({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={{ padding: "5px 6px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 11 }} />
    </label>
  );
}
